// The signed-in app shell (KSE-0191): owns the surface (mail | calendar),
// the viewport class, the shared chrome props, the launch intent from the
// manifest shortcuts, and the compact bottom bar. The mail surface stays
// mounted (hidden) while the calendar is up so an open composer and the
// thread list's position survive a switch; its keyboard shortcuts and history
// layers are suspended while inactive.

import { useCallback, useEffect, useMemo, useState } from "react";

import type { SessionStatus, StoredAccount } from "../auth";
import { useAuth } from "../auth";
import { CalendarShell } from "../calendar/CalendarShell";
import { useShell, useShellSnapshot, useShellStorage } from "../core/shell";
import { AppHeader, BottomBar, type ChromeProps, type EngineBadge } from "./AppChrome";
import { PasskeyBanner, StorageBanner } from "./Banners";
import { MailShell } from "./MailShell";
import {
  NAVIGATE_EVENT,
  parseLaunchIntent,
  readStoredSurface,
  SURFACE_STORAGE_KEY,
  type Surface,
  stripLaunchParams,
} from "./navigation";
import { useViewport } from "./useViewport";

const CALENDARS_CAPABILITY = "urn:ietf:params:jmap:calendars";
// The SW → page message posted when a thread notification is clicked
// (src/core/notify.ts NOTIFICATION_OPEN): the selected thread lands in the
// snapshot, so the mail surface must be the one on screen.
const NOTIFICATION_OPEN_MESSAGE = "lately-notification-open";

function persistSurface(surface: Surface) {
  try {
    localStorage.setItem(SURFACE_STORAGE_KEY, surface);
  } catch {
    // preference only
  }
}

export function AppShell({
  username,
  accounts,
  activeIndex,
  session,
  onSwitchAccount,
  onAddAccount,
  onSignOut,
}: {
  username: string;
  accounts: StoredAccount[];
  activeIndex: number;
  session: SessionStatus;
  onSwitchAccount: (index: number) => void;
  onAddAccount: () => void;
  onSignOut: () => void;
}) {
  const shell = useShell();
  const snapshot = useShellSnapshot(shell);
  const storage = useShellStorage(shell);
  const { takeoverStorage } = useAuth();
  const { compact } = useViewport();

  // The launch intent is read once, before the first render, so the surface
  // state starts where the shortcut pointed and the composer request is
  // pending from the first mail render.
  const [launch] = useState(() =>
    typeof window === "undefined"
      ? { compose: false, surface: null }
      : parseLaunchIntent(window.location.search),
  );
  const [surface, setSurfaceState] = useState<Surface>(
    () =>
      launch.surface ??
      readStoredSurface((key) =>
        typeof localStorage === "undefined" ? null : localStorage.getItem(key),
      ),
  );
  const [composeRequested, setComposeRequested] = useState(launch.compose);
  const [messageRequested, setMessageRequested] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || (!launch.compose && !launch.surface)) {
      return;
    }
    const { pathname, search, hash } = window.location;
    const stripped = stripLaunchParams(`${pathname}${search}${hash}`);
    if (stripped !== `${pathname}${search}${hash}`) {
      history.replaceState(history.state, "", stripped);
    }
  }, [launch]);

  const setSurface = useCallback((next: Surface) => {
    setSurfaceState(next);
    persistSurface(next);
  }, []);

  // Calendar is offered when the active account (by storage key) supports
  // calendars — the core's flag, or the raw session capability.
  const activeAccount = useMemo(() => {
    const byKey = snapshot.activeAccountStorageKey
      ? snapshot.accounts.find((account) => account.storageKey === snapshot.activeAccountStorageKey)
      : undefined;
    return byKey ?? snapshot.accounts.find((account) => account.isActive) ?? null;
  }, [snapshot.accounts, snapshot.activeAccountStorageKey]);
  const calendarAvailable =
    activeAccount !== null &&
    (activeAccount.supportsCalendars ||
      Boolean(activeAccount.session?.session.capabilities?.[CALENDARS_CAPABILITY]));
  const effectiveSurface: Surface = calendarAvailable ? surface : "mail";

  // In-app navigation requests: `window.dispatchEvent(new CustomEvent(
  // "lately-navigate", {detail: {surface, compose, openMessage}}))`, and the
  // service worker's notification-click message (the thread selection follows
  // in the snapshot; the mail surface has to be showing, and on a phone the
  // message pane open, for it to be seen).
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onNavigate = (event: Event) => {
      const detail = (
        event as CustomEvent<{ surface?: unknown; compose?: unknown; openMessage?: unknown }>
      ).detail;
      if (detail?.surface === "mail" || detail?.surface === "calendar") {
        setSurface(detail.surface);
      }
      if (detail?.compose === true) {
        setSurface("mail");
        setComposeRequested(true);
      }
      if (detail?.openMessage === true) {
        setSurface("mail");
        setMessageRequested(true);
      }
    };
    const onWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === NOTIFICATION_OPEN_MESSAGE) {
        setSurface("mail");
        setMessageRequested(true);
      }
    };
    window.addEventListener(NAVIGATE_EVENT, onNavigate);
    navigator.serviceWorker?.addEventListener("message", onWorkerMessage);
    return () => {
      window.removeEventListener(NAVIGATE_EVENT, onNavigate);
      navigator.serviceWorker?.removeEventListener("message", onWorkerMessage);
    };
  }, [setSurface]);

  const live = shell.hasSnapshot;
  const engineBadge: EngineBadge | null =
    shell.engine === "swift-shell"
      ? live
        ? {
            label: "swift-shell",
            title: `Live snapshots from the shared Swift core (WebAssembly, in a worker${
              shell.storage ? `, ${shell.storage} storage` : ""
            }${shell.abiVersion !== null ? `, abi ${shell.abiVersion}` : ""})`,
          }
        : null
      : {
          label: "mock-shell",
          title: "Fixture engine — the Swift core is not bundled in this build",
        };
  const status =
    session.status === "opening" ? "Connecting…" : snapshot.isRefreshing ? "Syncing…" : null;

  const chrome: ChromeProps = {
    compact,
    engineBadge,
    status,
    surface: effectiveSurface,
    calendarAvailable,
    onSurface: setSurface,
    account: {
      accounts,
      activeIndex,
      username,
      onSwitch: onSwitchAccount,
      onAdd: onAddAccount,
      onSignOut,
    },
  };

  const banners = (
    <>
      <StorageBanner storage={storage} onUseThisTab={takeoverStorage} />
      <PasskeyBanner />
    </>
  );

  return (
    <div
      data-surface-active={effectiveSurface}
      className="flex h-full flex-col bg-background text-foreground"
    >
      <MailShell
        chrome={chrome}
        active={effectiveSurface === "mail"}
        session={session}
        username={username}
        banners={banners}
        composeRequested={composeRequested}
        onComposeHandled={() => setComposeRequested(false)}
        messageRequested={messageRequested}
        onMessageHandled={() => setMessageRequested(false)}
      />
      {effectiveSurface === "calendar" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <AppHeader chrome={chrome} />
          {banners}
          <div className="flex min-h-0 flex-1 flex-col">
            <CalendarShell compact={compact} active={effectiveSurface === "calendar"} />
          </div>
        </div>
      ) : null}
      {compact && calendarAvailable ? (
        <BottomBar surface={effectiveSurface} onSurface={setSurface} />
      ) : null}
    </div>
  );
}
